import React, { useState, useContext } from "react";
import {
  Comment,
  updateComment,
  deleteComment,
} from "../api-requests/Comments";
import { UserContext } from "../context/UserContext";
import CommentComposer from "./CommentComposer";

interface CommentItemProps {
  comment: Comment;
  targetType: "debate" | "list" | "list_item";
  targetId: string;
  onCommentUpdated: (comment: Comment) => void;
  onCommentDeleted: (commentId: string) => void;
  onReplyAdded: (comment: Comment) => void;
  isReply?: boolean;
}

const CommentItem: React.FC<CommentItemProps> = ({
  comment,
  targetType,
  targetId,
  onCommentUpdated,
  onCommentDeleted,
  onReplyAdded,
  isReply = false,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isReplying, setIsReplying] = useState(false);
  const [editBody, setEditBody] = useState(comment.body);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { user } = useContext(UserContext);

  const isOwner = user && user.id === comment.userId;
  const canEdit = isOwner && !comment.isDeleted;
  const canDelete = isOwner && !comment.isDeleted;

  // Format date for display
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);

    if (diffInHours < 1) {
      const diffInMinutes = Math.floor(diffInHours * 60);
      return diffInMinutes <= 1 ? "just now" : `${diffInMinutes}m ago`;
    } else if (diffInHours < 24) {
      return `${Math.floor(diffInHours)}h ago`;
    } else if (diffInHours < 24 * 7) {
      return `${Math.floor(diffInHours / 24)}d ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  const handleEditSave = async () => {
    if (!editBody.trim()) {
      setError("Comment cannot be empty");
      return;
    }

    if (editBody.length > 1000) {
      setError("Comment cannot exceed 1000 characters");
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const response = await updateComment(comment.id, editBody.trim());
      if (response.success && response.comment) {
        onCommentUpdated(response.comment);
        setIsEditing(false);
      } else {
        setError(response.message || "Failed to update comment");
      }
    } catch (err) {
      setError("An error occurred while updating your comment");
      console.error("Error updating comment:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditCancel = () => {
    setEditBody(comment.body);
    setIsEditing(false);
    setError(null);
  };

  const handleDelete = async () => {
    if (!window.confirm("Are you sure you want to delete this comment?")) {
      return;
    }

    setIsDeleting(true);
    setError(null);

    try {
      const response = await deleteComment(comment.id);
      if (response.success) {
        onCommentDeleted(comment.id);
      } else {
        setError(response.message || "Failed to delete comment");
      }
    } catch (err) {
      setError("An error occurred while deleting your comment");
      console.error("Error deleting comment:", err);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleReplyAdded = (newReply: Comment) => {
    onReplyAdded(newReply);
    setIsReplying(false);
  };

  // Generate avatar from bg_color or first letter of username
  const getAvatarStyle = () => {
    if (comment.user.avatar && comment.user.avatar.startsWith("#")) {
      return { backgroundColor: comment.user.avatar };
    }
    // Fallback to a color based on user ID
    const colors = [
      "#EF4444",
      "#F97316",
      "#EAB308",
      "#22C55E",
      "#06B6D4",
      "#3B82F6",
      "#8B5CF6",
      "#EC4899",
    ];
    const colorIndex = comment.userId.length % colors.length;
    return { backgroundColor: colors[colorIndex] };
  };

  const getInitials = () => {
    const name = comment.user.username || "User";
    return name.charAt(0).toUpperCase();
  };

  if (comment.isDeleted && (!comment.replies || comment.replies.length === 0)) {
    return null; // Don't render deleted comments with no replies
  }

  return (
    <div className={`${isReply ? "ml-8 border-l-2 border-gray-700 pl-4" : ""}`}>
      {/* Comment Content */}
      <div className="bg-gray-700 rounded-lg p-4 mb-3">
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3">
            {/* Avatar */}
            <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0">
              {comment.user.avatar && !comment.user.avatar.startsWith("#") ? (
                <img
                  src={comment.user.avatar}
                  alt={`${comment.user.username}'s avatar`}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div
                  className="w-full h-full flex items-center justify-center text-white text-sm font-semibold"
                  style={getAvatarStyle()}
                >
                  {getInitials()}
                </div>
              )}
            </div>

            {/* User info */}
            <div>
              <div className="flex items-center gap-2">
                <span className="font-medium text-white">
                  {comment.user.username || "Anonymous"}
                </span>
                {comment.updatedAt !== comment.createdAt && (
                  <span className="text-xs text-gray-400">(edited)</span>
                )}
              </div>
              <span className="text-xs text-gray-400">
                {formatDate(comment.updatedAt)}
              </span>
            </div>
          </div>

          {/* Actions Menu */}
          {(canEdit || canDelete) && (
            <div className="flex items-center gap-2">
              {canEdit && (
                <button
                  onClick={() => setIsEditing(true)}
                  disabled={isDeleting}
                  className="text-xs text-gray-400 hover:text-gray-300 transition-colors duration-200 disabled:opacity-50"
                >
                  Edit
                </button>
              )}
              {canDelete && (
                <button
                  onClick={handleDelete}
                  disabled={isDeleting}
                  className="text-xs text-red-400 hover:text-red-300 transition-colors duration-200 disabled:opacity-50"
                >
                  {isDeleting ? "Deleting..." : "Delete"}
                </button>
              )}
            </div>
          )}
        </div>

        {/* Comment Body */}
        {isEditing ? (
          <div className="space-y-3">
            <textarea
              value={editBody}
              onChange={(e) => setEditBody(e.target.value)}
              disabled={isSubmitting}
              rows={3}
              className={`w-full bg-gray-600 border border-gray-500 rounded px-3 py-2 text-white placeholder-gray-400 resize-vertical focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                isSubmitting ? "opacity-50 cursor-not-allowed" : ""
              }`}
            />

            {error && <div className="text-red-400 text-sm">{error}</div>}

            <div className="flex items-center gap-2">
              <button
                onClick={handleEditSave}
                disabled={
                  isSubmitting || !editBody.trim() || editBody.length > 1000
                }
                className={`px-3 py-1 rounded text-sm font-medium ${
                  isSubmitting || !editBody.trim() || editBody.length > 1000
                    ? "bg-gray-600 text-gray-400 cursor-not-allowed"
                    : "bg-blue-600 text-white hover:bg-blue-700"
                }`}
              >
                {isSubmitting ? "Saving..." : "Save"}
              </button>
              <button
                onClick={handleEditCancel}
                disabled={isSubmitting}
                className="px-3 py-1 text-sm text-gray-300 hover:text-white disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div>
            <p className="text-gray-200 whitespace-pre-wrap break-words">
              {comment.body}
            </p>

            {/* Reply Button */}
            {user && !isReply && !comment.isDeleted && (
              <div className="mt-3 pt-3 border-t border-gray-600">
                <button
                  onClick={() => setIsReplying(!isReplying)}
                  className="text-sm text-gray-400 hover:text-gray-300 transition-colors duration-200"
                >
                  {isReplying ? "Cancel Reply" : "Reply"}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Error Display */}
        {error && !isEditing && (
          <div className="mt-2 text-red-400 text-sm">{error}</div>
        )}
      </div>

      {/* Reply Composer */}
      {isReplying && (
        <div className="mb-4">
          <CommentComposer
            targetType={targetType}
            targetId={targetId}
            parentId={comment.id}
            onCommentAdded={handleReplyAdded}
            placeholder="Write a reply..."
            buttonText="Reply"
            autoFocus={true}
            onCancel={() => setIsReplying(false)}
          />
        </div>
      )}

      {/* Replies */}
      {comment.replies && comment.replies.length > 0 && (
        <div className="space-y-3">
          {comment.replies.map((reply) => (
            <CommentItem
              key={reply.id}
              comment={reply}
              targetType={targetType}
              targetId={targetId}
              onCommentUpdated={onCommentUpdated}
              onCommentDeleted={onCommentDeleted}
              onReplyAdded={onReplyAdded}
              isReply={true}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default CommentItem;
